import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Row, Col, Statistic, DatePicker, Space, Tag, message, Popconfirm, Empty, Modal, Form, Input, InputNumber, Select, Upload, Tooltip, Alert } from 'antd';
import { ReloadOutlined, DeleteOutlined, PlusOutlined, DownloadOutlined, CameraOutlined, UploadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch, apiDownload, onDataChanged, getToken } from '../utils/api.js';
import { yuan } from '../utils/format.js';
import { prodCode } from '../utils/prodCode.js';

const WEEK = ['日', '一', '二', '三', '四', '五', '六'];
const PACK_UNITS = [
  { value: '袋', label: '袋' },
  { value: '箱', label: '箱' },
  { value: '件', label: '件（散装）' },
];
// 日期 → 中文（8月18日 星期二）
function zhDate(d) {
  const p = String(d).split('-');
  if (p.length !== 3) return d;
  const dayjsObj = dayjs(d);
  return `${Number(p[1])}月${Number(p[2])}日（星期${WEEK[dayjsObj.day()]}）`;
}
function zhMonth(m) {
  const p = String(m).split('-');
  return p.length === 2 ? `${p[0]}年${Number(p[1])}月` : m;
}

export default function DailyInbound() {
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [exporting, setExporting] = useState(false);
  const [records, setRecords] = useState([]);
  const [groups, setGroups] = useState({});
  const [stats, setStats] = useState({ total_records: 0, total_qty: 0, total_days: 0, product_count: 0, manual_count: 0 });
  const [manualModal, setManualModal] = useState(false);
  const [manualForm] = Form.useForm();
  const [inventoryOpts, setInventoryOpts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // 拍照留底：受控 fileList + 上传状态
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 实时监听表单：包装明细，用于报数总数即时计算
  const items = Form.useWatch('items', manualForm);

  const loadData = useCallback((m) => {
    const mm = m || month;
    apiFetch('/api/daily-inbound?month=' + mm).then(r => r.json()).then(d => {
      setRecords(d.data || []); setGroups(d.groups || {}); setStats(d.stats || {});
    }).catch(() => {});
  }, [month]);
  useEffect(() => { loadData(); }, [loadData]);
  // 手动入库商品选项：现有库存条目 + 商品成本产品（抽成函数，数据变更时刷新；带单价供自动带出）
  const loadInventoryOpts = useCallback(() => {
    apiFetch('/api/inventory').then(r => r.json()).then(d => {
      const inv = d.data || [];
      const set = {};
      // 库存条目：统一归一化到上级编码（历史可能有带数量后缀的脏数据，一并防御）
      for (const i of inv) {
        const code = prodCode(i.spec_code);
        if (!code) continue;
        if (!set[code]) set[code] = { name: i.product_name || code, cost: i.unit_cost ?? i.product_cost ?? 0 };
      }
      apiFetch('/api/products').then(r => r.json()).then(pd => {
        for (const p of (pd.data || [])) {
          const list = p.children && p.children.length ? p.children : (p.spec_code ? [p] : []);
          for (const c of list) {
            const raw = String(c.spec_code || '').trim();
            if (!raw) continue;
            // 防输错：只保留上级产品编码，带数量后缀的下级编码（-1000/-500 等）一律归一化，不进入入库选项
            const code = prodCode(raw);
            if (!code) continue;
            const cost = c.product_cost ?? p.product_cost ?? 0;
            // 商品成本单价优先（库存 unit_cost 常为 0，商品成本有单价时覆盖）
            if (!set[code]) set[code] = { name: p.product_name || code, cost };
            else if (!(set[code].cost > 0) && cost > 0) set[code] = { name: set[code].name || p.product_name || code, cost };
          }
        }
        setInventoryOpts(Object.entries(set).map(([code, v]) => ({ value: code, label: `${code}（${v.name}）`, cost: v.cost })));
      }).catch(() => {});
    }).catch(() => {});
  }, []);
  useEffect(() => { loadInventoryOpts(); }, [loadInventoryOpts]);
  // 供应商列表（手动入库选供应商，对账用）
  useEffect(() => {
    apiFetch('/api/suppliers').then(r => r.json()).then(d => setSuppliers(d.data || [])).catch(() => {});
  }, []);
  // 数据变更（新增产品等）→ 刷新台账 + 刷新产品选项
  useEffect(() => onDataChanged(() => { loadData(); loadInventoryOpts(); }), [loadData, loadInventoryOpts]);

  const delRecord = async (rec) => {
    if (rec.source === 'manual') {
      const res = await apiFetch('/api/daily-inbound/manual/' + rec.mid, { method: 'DELETE' }).then(r => r.json());
      if (res.success) { message.success('已删除手动入库，库存已回滚'); loadData(); }
      else message.error(res.error || '删除失败');
      return;
    }
    const q = `po_id=${rec.po_id}&item_idx=${rec.item_idx}&inbound_idx=${rec.inbound_idx}`;
    const res = await apiFetch('/api/daily-inbound?' + q, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除，库存已回滚'); loadData(); }
    else message.error(res.error || '删除失败');
  };

  // 实时算报数总数：Σ(单件数量 × 件数)
  const packedTotal = (items || []).reduce((a, it) => a + (Number(it && it.pack_qty) || 0) * (Number(it && it.pack_count) || 0), 0);
  const totalUnits = (items || []).reduce((a, it) => a + (Number(it && it.pack_count) || 0), 0);

  // 多图上传：把未上传的图批量 POST 到 /api/upload/images
  const uploadImages = async () => {
    const toUpload = fileList.filter(f => !f.url && f.originFileObj);
    if (!toUpload.length) { message.warning('没有待上传的图片'); return; }
    const form = new FormData();
    toUpload.forEach(f => form.append('images', f.originFileObj));
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    setUploading(true);
    try {
      const res = await fetch('/api/upload/images', { method: 'POST', body: form, headers });
      const j = await res.json();
      if (j.success && Array.isArray(j.urls)) {
        let i = 0;
        setFileList(prev => prev.map(f => (!f.url && f.originFileObj && i < j.urls.length) ? { ...f, url: j.urls[i++], status: 'done' } : f));
        message.success(`已上传 ${j.urls.length} 张留底图`);
      } else {
        message.error((j && j.error) || '上传失败');
      }
    } catch (e) {
      message.error('上传失败：' + (e.message || '网络错误'));
    } finally {
      setUploading(false);
    }
  };
  // 选择图片时做清晰度提醒（不硬拦）：宽或高 < 1200px 提示
  const checkRes = (file) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.naturalWidth < 1200 || img.naturalHeight < 1200) {
        message.warning(`图片「${file.name}」分辨率仅 ${img.naturalWidth}×${img.naturalHeight}，建议用原图（≥1200px）重拍，否则留底可能看不清数量`);
      }
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  };

  const submitManual = async () => {
    const v = await manualForm.validateFields();
    // 图片必须全部上传完成才能提交（避免留底丢失）
    if (fileList.some(f => !f.url)) { message.warning('还有图片未上传，请先点击「上传留底图」'); return; }
    // 自动带出商品名称（从选项数据取）
    const opt = inventoryOpts.find(o => o.value === v.spec_code);
    const name = opt ? (opt.label.match(/（(.+)）/) || [])[1] || '' : '';
    const body = {
      spec_code: v.spec_code,
      product_name: name,
      date: v.date ? v.date.format('YYYY-MM-DD') : undefined,
      operator: v.operator,
      supplier_id: v.supplier_id,
      unit_cost: v.unit_cost,
      note: v.note,
      items: (v.items || []).map(it => ({ pack_unit: it.pack_unit, pack_qty: it.pack_qty, pack_count: it.pack_count })),
      images: fileList.map(f => f.url).filter(Boolean),
    };
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/daily-inbound/manual', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(r => r.json());
      if (res.success) { message.success('手动入库成功，库存已增加'); setManualModal(false); manualForm.resetFields(); setFileList([]); loadData(); }
      else message.error(res.error || '入库失败');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: '来源', dataIndex: 'source', width: 110,
      render: (s, r) => s === 'manual'
        ? <Tag color="#EF9F27">手动入库</Tag>
        : <Tag color="#7F77DD">PO-{String(r.po_id).padStart(3, '0')}</Tag> },
    { title: '商品编码', dataIndex: 'spec_code', width: 150, render: v => <b>{v}</b> },
    { title: '商品名称', dataIndex: 'product_name', ellipsis: true },
    { title: '供应商', dataIndex: 'supplier_name', width: 110, render: v => v ? <Tag style={{ margin: 0 }}>{v}</Tag> : <span style={{ color: '#bfbfbf' }}>—</span> },
    { title: '入库数量', dataIndex: 'quantity', width: 90, align: 'right',
      render: v => <span style={{ color: 'var(--brand-strong)', fontWeight: 700 }}>{v}</span> },
    { title: '单价(元)', dataIndex: 'unit_cost', width: 90, align: 'right', render: v => v ? Number(v).toFixed(4) : <span style={{ color: '#bfbfbf' }}>—</span> },
    { title: '金额(元)', dataIndex: 'amount', width: 100, align: 'right',
      render: v => <b style={{ color: 'var(--brand-strong)' }}>{yuan(v)}</b> },
    { title: '入库人', dataIndex: 'operator', width: 100, render: v => v || '—' },
    { title: '留底', dataIndex: 'images', width: 90,
      render: (imgs) => {
        const im = (imgs || []).length;
        return im > 0 ? <Tooltip title={`留底图 ${im} 张`}><Tag icon={<CameraOutlined />} color="blue">{im}</Tag></Tooltip> : <span style={{ color: '#bfbfbf' }}>—</span>;
      } },
    { title: '入库时间', dataIndex: 'date', width: 120 },
    { title: '备注', dataIndex: 'note', ellipsis: true, render: v => v || '' },
    { title: '操作', width: 80, align: 'center',
      render: (_, rec) => (
        <Popconfirm title="删除该条入库记录？库存将自动回滚" onConfirm={() => delRecord(rec)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ) },
  ];

  // 按日分组渲染多个小表
  const dayGroups = Object.entries(groups).sort((a, b) => String(b[0]).localeCompare(String(a[0])));

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={4}>
          <div className="stat-card" style={{ '--sc': 'var(--brand)', padding: '16px 18px' }}>
            <div className="stat-label">当月入库笔数</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{stats.total_records}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>笔</span></div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="stat-card" style={{ '--sc': 'var(--brand-strong)', padding: '16px 18px' }}>
            <div className="stat-label">当月入库总量</div>
            <div className="stat-value" style={{ fontSize: 22, color: 'var(--brand-strong)' }}>{stats.total_qty}</div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="stat-card" style={{ '--sc': '#378ADD', padding: '16px 18px' }}>
            <div className="stat-label">入库天数</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{stats.total_days}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>天</span></div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="stat-card" style={{ '--sc': '#7F77DD', padding: '16px 18px' }}>
            <div className="stat-label">涉及商品数</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{stats.product_count}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>种</span></div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="stat-card" style={{ '--sc': 'var(--brand-strong)', padding: '16px 18px', background: '#FAEEDA' }}>
            <div className="stat-label">当月入库总金额(元)</div>
            <div className="stat-value" style={{ fontSize: 22, color: 'var(--brand-strong)', fontWeight: 700 }}>¥{yuan(stats.total_amount)}</div>
          </div>
        </Col>
        </Row>

      <Card title={`每日入库台账（${zhMonth(month)} · 采购入库 + 手动入库）`}
        extra={
          <Space wrap>
            <DatePicker picker="month" format="YYYY年M月" value={month ? dayjs(month) : null} onChange={(d) => { const m = d ? d.format('YYYY-MM') : dayjs().format('YYYY-MM'); setMonth(m); loadData(m); }} />
            <Button icon={<PlusOutlined />} type="primary" onClick={() => { manualForm.resetFields(); setFileList([]); manualForm.setFieldsValue({ date: dayjs(), operator: '', items: [{ pack_unit: '袋', pack_qty: null, pack_count: null }] }); setManualModal(true); }}>手动入库</Button>
            <Button icon={<DownloadOutlined />} loading={exporting} onClick={async () => { setExporting(true); try { await apiDownload('/api/daily-inbound/export?month=' + month + '&t=' + Date.now(), 'daily-inbound-' + month + '.xlsx'); } catch (e) { message.error(e.message || '导出失败'); } finally { setExporting(false); } }}>导出报表</Button>
            <Button icon={<ReloadOutlined />} onClick={() => loadData()}>刷新</Button>
          </Space>
        }>
        {records.length === 0 && <Empty description="当月暂无入库记录" style={{ padding: 24 }} />}
        {dayGroups.map(([date, recs]) => (
          <Card key={date} size="small" style={{ marginBottom: 12 }}
            title={
              <Space size="middle" wrap>
                <b style={{ color: 'var(--brand-strong)', fontSize: 14 }}>{zhDate(date)}</b>
                <Tag color="#EF9F27">{recs.length} 笔</Tag>
                <Tag color="#7F77DD">合计 {recs.reduce((a, r) => a + Number(r.quantity), 0)}</Tag>
                <Tag color="#378ADD">金额 ¥{yuan(recs.reduce((a, r) => a + Number(r.amount || 0), 0))}</Tag>
              </Space>
            }>
            <Table rowKey={(r) => `${r.source}-${r.po_id}-${r.item_idx}-${r.inbound_idx}-${r.mid}`} size="small" pagination={false}
              columns={columns} dataSource={recs} scroll={{ x: 1100 }} />
          </Card>
        ))}
      </Card>

      <Modal title="手动入库（拍照留底）" open={manualModal} width={720} okText="确认入库" cancelText="取消"
        onOk={submitManual} onCancel={() => setManualModal(false)} confirmLoading={submitting}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }}
          message="按「单件数量 × 件数」录入，并拍照留底，杜绝入库数量与实到不符。" />
        <Form form={manualForm} layout="vertical">
          <Form.Item name="spec_code" label="商品编码" rules={[{ required: true, message: '请选择商品' }]}>
            <Select showSearch placeholder="选择商品编码" options={inventoryOpts} filterOption={(i, o) => (o.label || '').includes(i)}
              onChange={(v) => {
                const o = inventoryOpts.find(x => x.value === v);
                // 选择产品后自动带出商品成本页录入的单价（可修改）
                if (o && o.cost) manualForm.setFieldsValue({ unit_cost: o.cost });
              }} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}><Form.Item name="date" label="入库日期" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="operator" label="入库人" rules={[{ required: true, message: '请填写入库人' }]}><Input placeholder="入库工人姓名" /></Form.Item></Col>
          </Row>

          <Form.Item label={<span>包装明细 <span style={{ color: '#8C8178', fontWeight: 400 }}>（单件数量 × 件数，支持混合规格：如 500/袋×5 + 2000/袋×3）</span></span>} required style={{ marginBottom: 4 }}>
            <Form.List name="items" rules={[{ validator: async (_, v) => { if (!v || !v.length) throw new Error('至少填写一行包装明细'); } }]}>
              {(fields, { add, remove }) => (
                <>
                  <div style={{ display: 'flex', gap: 8, padding: '4px 0 8px', color: '#8C8178', fontSize: 12 }}>
                    <span style={{ width: 96 }}>包装单位</span>
                    <span style={{ width: 140 }}>单件数量</span>
                    <span style={{ width: 24 }}></span>
                    <span style={{ width: 120 }}>件数</span>
                    <span>操作</span>
                  </div>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'baseline' }}>
                      <Form.Item {...restField} name={[name, 'pack_unit']} style={{ width: 96, marginBottom: 0 }} rules={[{ required: true, message: '选单位' }]}>
                        <Select options={PACK_UNITS} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'pack_qty']} style={{ width: 140, marginBottom: 0 }} rules={[{ required: true, message: '填单件数量' }]}>
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="如 500/1000/2000" />
                      </Form.Item>
                      <span style={{ width: 24, textAlign: 'center', color: '#8C8178' }}>×</span>
                      <Form.Item {...restField} name={[name, 'pack_count']} style={{ width: 120, marginBottom: 0 }} rules={[{ required: true, message: '填件数' }]}>
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="件数" />
                      </Form.Item>
                      {fields.length > 1
                        ? <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                        : <span style={{ width: 32 }} />}
                    </div>
                  ))}
                  <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ pack_unit: '袋', pack_qty: null, pack_count: null })}>添加一行包装</Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          <div style={{ margin: '8px 0 16px', padding: '10px 14px', background: '#FFF8F4', borderRadius: 8, border: '1px solid #F3DFD2' }}>
            <b>报数总数：</b>
            <span style={{ color: 'var(--brand-strong)', fontWeight: 700, fontSize: 16 }}>{packedTotal}</span>
            <span style={{ color: '#8C8178', marginLeft: 8, fontSize: 12 }}>（共 {totalUnits} 件/袋/箱）</span>
          </div>

          <Form.Item label={<span>拍照留底 <span style={{ color: '#8C8178', fontWeight: 400 }}>（最多 9 张，建议原图 ≥1200px，拍清包装与数量）</span></span>} required style={{ marginBottom: 4 }}>
            <Upload
              listType="picture-card"
              fileList={fileList}
              multiple
              maxCount={9}
              accept="image/*"
              onChange={({ fileList: fl }) => setFileList(fl)}
              beforeUpload={(f) => { checkRes(f); return false; }}
            >
              {fileList.length >= 9 ? null : <div><PlusOutlined /><div style={{ marginTop: 4 }}>选择图片</div></div>}
            </Upload>
            <div style={{ marginTop: 8 }}>
              <Button icon={<UploadOutlined />} loading={uploading} onClick={uploadImages} disabled={!fileList.length}>上传留底图</Button>
              <span style={{ color: '#8C8178', fontSize: 12, marginLeft: 12 }}>已选 {fileList.length} 张，已上传 {fileList.filter(f => f.url).length} 张</span>
            </div>
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}><Form.Item name="unit_cost" label="单价(元/件)" extra={<span style={{ fontSize: 11 }}>选商品自动带出，可修改</span>}><InputNumber min={0} precision={4} style={{ width: '100%' }} placeholder="自动带出" /></Form.Item></Col>
            <Col span={12}><Form.Item name="supplier_id" label="供应商" extra={<span style={{ fontSize: 11 }}>对账用，可选</span>}><Select allowClear showSearch placeholder="选择供应商" options={suppliers.map(s => ({ value: s.id, label: s.name }))} filterOption={(i, o) => (o.label || '').includes(i)} /></Form.Item></Col>
            <Col span={12}><Form.Item name="note" label="备注（可选）"><Input /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
